import { afterNextRender, Component, computed, DestroyRef, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { AppStore } from '@core/services';
import { headerLogoPath } from '@core/utils/logo.utils';

@Component({
  selector: 'app-header',
  imports: [CommonModule, NgbTooltip],
  templateUrl: './header.html',
  styleUrl: './header.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true
})
export class Header {
  private static readonly MOBILE_BREAKPOINT = 576;

  private readonly appStore = inject(AppStore);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly isMobileViewport = signal(false);

  readonly logoPath = computed(() => {
    const isDark = this.appStore.effectiveTheme() === 'dark';
    return headerLogoPath(isDark, this.isMobileViewport());
  });

  constructor() {
    afterNextRender(() => {
      const updateViewport = (): void => {
        if (typeof window === 'undefined') {
          this.isMobileViewport.set(false);
          return;
        }

        this.isMobileViewport.set(window.innerWidth <= Header.MOBILE_BREAKPOINT);
      };

      updateViewport();
      window.addEventListener('resize', updateViewport);
      this.destroyRef.onDestroy(() => window.removeEventListener('resize', updateViewport));
    });
  }

  readonly fullName = computed(() => {
    const user = this.appStore.user();
    if (!user) {
      return '';
    }
    const nome = user.nome?.trim() ?? '';
    const cognome = user.cognome?.trim() ?? '';
    return `${nome} ${cognome}`.trim();
  });

  readonly firstName = computed(() => {
    const user = this.appStore.user();
    return user?.nome?.trim() ?? '';
  });

  readonly lastName = computed(() => {
    const user = this.appStore.user();
    return user?.cognome?.trim() ?? '';
  });

  readonly showProfileWarning = computed(() => {
    const user = this.appStore.user();
    if (!user) {
      return false;
    }

    const codiceFiscale = user.codice_fiscale?.trim() ?? '';
    const cellulare = user.cellulare?.trim() ?? '';
    return !codiceFiscale || !cellulare;
  });

  goToSettings(): void {
    this.router.navigateByUrl('/settings');
  }

  goToLandingPage(): void {
    if (!this.appStore.isAuthenticated()) {
      this.router.navigateByUrl('/login');
      return;
    }

    this.router.navigateByUrl('/landing-page');
  }
}
